// C:\Users\aleja\Code\inventory_analytics\src\front\src\pages\MachineDetail.jsx
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Context } from "../store/appContext";
import "../styles/machineCatalog.css";

const TYPES = [["COMPONENTE", "Componente"], ["CONSUMIBLE", "Consumible"], ["DESGASTE", "Desgaste"], ["AUXILIAR", "Auxiliar"]];
const CRITICALITIES = [["SIN_CLASIFICAR", "Sin clasificar"], ["BAJA", "Baja"], ["MEDIA", "Media"], ["ALTA", "Alta"], ["CRITICA", "Crítica"]];
const EMPTY_FILTERS = { q: "", relationship_type: "", criticality: "", is_active: "true" };
const EMPTY_FORM = { relationship_type: "COMPONENTE", quantity_required: "1", position_reference: "", usage_description: "", criticality: "SIN_CLASIFICAR", source: "MANUAL", source_reference: "", notes: "", is_active: true };

const text = (value) => String(value ?? "").trim();
const array = (value) => Array.isArray(value) ? value : [];
const optional = (value) => text(value) || null;
const number = (value) => new Intl.NumberFormat("es-ES", { maximumFractionDigits: 3 }).format(Number(value || 0));
const items = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  for (const key of ["items", "results", ...keys]) if (Array.isArray(payload?.[key])) return payload[key];
  return [];
};
const paginationOf = (payload, page, perPage) => {
  const meta = payload?.pagination || payload?.meta || {};
  const total = Number(meta.total ?? meta.total_items ?? payload?.total ?? 0);
  const currentPerPage = Number(meta.per_page ?? meta.page_size ?? payload?.per_page ?? perPage);
  const totalPages = Number(meta.total_pages ?? meta.pages ?? payload?.total_pages ?? Math.max(1, Math.ceil(total / Math.max(currentPerPage, 1))));
  return { page: Number(meta.page ?? payload?.page ?? page) || page, perPage: currentPerPage || perPage, total: total || 0, totalPages: totalPages || 1 };
};
const materialOf = (relation) => relation?.material || relation?.material_summary || relation?.spare_part || {};
const materialRecordId = (item) => item?.material_record_id ?? item?.material_id_internal ?? item?.record_id ?? item?.material?.id ?? null;
const materialCode = (item) => text(item?.material_code ?? item?.code ?? item?.material_id ?? item?.material?.code ?? item?.material?.material_id);
const materialDescription = (item) => text(item?.description ?? item?.material_description ?? item?.visor_description ?? item?.master_description ?? item?.material?.description) || "Sin descripción";
const stockOf = (relation) => relation?.stock_total ?? relation?.operational?.stock_total ?? relation?.material_operational?.stock_total ?? 0;
const locationsOf = (relation) => array(relation?.locations ?? relation?.stock_locations ?? relation?.operational?.locations ?? relation?.material_operational?.locations);
const documentsOf = (relation) => Number(relation?.document_count ?? relation?.documents_count ?? relation?.operational?.document_count ?? relation?.material_operational?.document_count ?? 0);
const errorOf = (result, fallback) => result?.error || result?.message || fallback;
const typeLabel = (value) => TYPES.find(([code]) => code === value)?.[1] || value || "Sin dato";
const criticalityLabel = (value) => CRITICALITIES.find(([code]) => code === value)?.[1] || value || "Sin dato";
const locationLabel = (location) => [location?.branch_name, location?.warehouse_id, location?.column_location, location?.shelf, location?.bin, location?.location].map(text).filter(Boolean).join(" · ") || "Sin ubicación";

const RELATED_GROUP_DEFINITIONS = [
  {
    key: "alias_matches",
    title: "Identificados por alias",
    description:
      "El nombre completo de la máquina aparece en el alias del maestro ORMA.",
    badge: "Alias exacto",
  },
  {
    key: "related_matches",
    title: "Relacionados por fabricante, modelo o denominación",
    description:
      "Coincidencias específicas encontradas en alias, marca, modelo, descripción o referencias.",
    badge: "Relacionado",
  },
  {
    key: "shared_matches",
    title: "Compatibles con varias máquinas",
    description:
      "Materiales compartidos por equipos equivalentes, fabricantes o modelos relacionados.",
    badge: "Compartido",
  },
  {
    key: "line_matches",
    title: "Materiales relacionados con la línea",
    description:
      "Coincidencias de la línea o unidad de fábrica, separadas de los repuestos específicos.",
    badge: "Línea",
  },
];

const relatedCandidateCode = (item) => text(item?.material_id ?? item?.code);
const relatedCandidateDescription = (item) =>
  text(item?.description ?? item?.master_description ?? item?.visor_description) ||
  "Sin descripción";
const relatedCandidateLocations = (item) => array(item?.locations);


const MachineDetail = () => {
  const { machine_id: machineId } = useParams();
  const navigate = useNavigate();
  const { store, actions } = useContext(Context);
  const isAdmin = store?.role === "admin";
  const [machine, setMachine] = useState(null);
  const [machineLoading, setMachineLoading] = useState(true);
  const [machineError, setMachineError] = useState("");
  const [relations, setRelations] = useState([]);
  const [relationsLoading, setRelationsLoading] = useState(true);
  const [relationsError, setRelationsError] = useState("");
  const [relatedMaterials, setRelatedMaterials] = useState({
    groups: {
      alias_matches: [],
      related_matches: [],
      shared_matches: [],
      line_matches: [],
    },
    summary: {},
  });
  const [relatedLoading, setRelatedLoading] = useState(true);
  const [relatedError, setRelatedError] = useState("");
  const [summary, setSummary] = useState({});
  const [pagination, setPagination] = useState({ page: 1, perPage: 20, total: 0, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [materialQuery, setMaterialQuery] = useState("");
  const [materialResults, setMaterialResults] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [materialSearching, setMaterialSearching] = useState(false);
  const [materialError, setMaterialError] = useState("");
  const machineRequest = useRef(0);
  const relationRequest = useRef(0);
  const relatedRequest = useRef(0);
  const materialRequest = useRef(0);

  const loadMachine = useCallback(async () => {
    const requestId = ++machineRequest.current;
    setMachineLoading(true);
    setMachineError("");
    const result = await actions.getMachine(machineId);
    if (machineRequest.current !== requestId) return;
    if (!result?.success) {
      setMachine(null);
      setMachineError(errorOf(result, "No se pudo cargar la ficha de la máquina"));
    } else {
      setMachine(result.data?.machine || result.data?.item || result.data || null);
    }
    setMachineLoading(false);
  }, [actions, machineId]);

  const loadRelations = useCallback(async (signal) => {
    const requestId = ++relationRequest.current;
    setRelationsLoading(true);
    setRelationsError("");
    const params = { page, per_page: pagination.perPage, sort_by: "material_code", sort_order: "asc" };
    Object.entries(appliedFilters).forEach(([key, value]) => { if (text(value)) params[key] = text(value); });
    const result = await actions.getMachineSpareParts(machineId, params, { signal });
    if (signal?.aborted || relationRequest.current !== requestId) return;
    if (!result?.success) {
      if (!result?.aborted) setRelationsError(errorOf(result, "No se pudieron cargar los repuestos asociados"));
      setRelations([]);
    } else {
      const payload = result.data || {};
      setRelations(items(payload, ["relations", "machine_spare_parts", "spare_parts"]));
      setSummary(payload?.summary || payload?.totals || payload?.meta?.summary || {});
      setPagination((current) => paginationOf(payload, page, current.perPage));
    }
    setRelationsLoading(false);
  }, [actions, appliedFilters, machineId, page, pagination.perPage]);

  const loadRelatedMaterials = useCallback(async (signal) => {
    const requestId = ++relatedRequest.current;
    setRelatedLoading(true);
    setRelatedError("");
    const result = await actions.getMachineRelatedMaterials(
      machineId,
      { per_group: 25 },
      { signal },
    );
    if (signal?.aborted || relatedRequest.current !== requestId) return;
    if (!result?.success) {
      if (!result?.aborted) {
        setRelatedError(
          errorOf(result, "No se pudieron descubrir los repuestos relacionados"),
        );
      }
      setRelatedMaterials({
        groups: {
          alias_matches: [],
          related_matches: [],
          shared_matches: [],
          line_matches: [],
        },
        summary: {},
      });
    } else {
      setRelatedMaterials(result.data || {});
    }
    setRelatedLoading(false);
  }, [actions, machineId]);

  useEffect(() => { loadMachine(); }, [loadMachine]);
  useEffect(() => {
    const controller = new AbortController();
    loadRelatedMaterials(controller.signal);
    return () => controller.abort();
  }, [loadRelatedMaterials]);
  useEffect(() => { const controller = new AbortController(); loadRelations(controller.signal); return () => controller.abort(); }, [loadRelations]);

  useEffect(() => {
    if (!formOpen || editing || text(materialQuery).length < 2 || selectedMaterial) {
      setMaterialResults([]);
      setMaterialSearching(false);
      return undefined;
    }
    const controller = new AbortController();
    const requestId = ++materialRequest.current;
    const timer = window.setTimeout(async () => {
      setMaterialSearching(true);
      setMaterialError("");
      const result = await actions.searchMachineSpareMaterials({ q: text(materialQuery), page: 1, per_page: 10, sort_by: "material_id", sort_order: "asc" }, { signal: controller.signal });
      if (controller.signal.aborted || materialRequest.current !== requestId) return;
      if (!result?.success) {
        if (!result?.aborted) setMaterialError(errorOf(result, "No se pudo buscar el material"));
        setMaterialResults([]);
      } else {
        setMaterialResults(items(result.data, ["materials", "parts", "rows"]));
      }
      setMaterialSearching(false);
    }, 300);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [actions, editing, formOpen, materialQuery, selectedMaterial]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setSelectedMaterial(null); setMaterialQuery(""); setMaterialResults([]); setMaterialError(""); setFormError(""); setFormOpen(true); };
  const openSuggestedMaterial = (item) => {
    const code = relatedCandidateCode(item);
    const description = relatedCandidateDescription(item);
    const recordId = item?.material_record_id;

    if (!recordId && !code) {
      setRelatedError("La sugerencia no contiene una identidad de material válida");
      return;
    }

    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      source: "MANUAL",
      source_reference: `CATALOGO_INTELIGENTE:${item?.match_type || "RELATED"}`,
      usage_description: item?.explanation || "",
    });
    setSelectedMaterial({
      material_record_id: recordId,
      material_code: code,
      description,
    });
    setMaterialQuery([code, description].filter(Boolean).join(" · "));
    setMaterialResults([]);
    setMaterialError("");
    setFormError("");
    setFormOpen(true);
  };
  const openEdit = (relation) => {
    const material = materialOf(relation);
    setEditing(relation);
    setSelectedMaterial({ ...material, material_record_id: relation?.material_id ?? materialRecordId(material), material_code: materialCode(material), description: materialDescription(material) });
    setForm({ relationship_type: relation?.relationship_type || "COMPONENTE", quantity_required: String(relation?.quantity_required ?? 1), position_reference: relation?.position_reference || "", usage_description: relation?.usage_description || "", criticality: relation?.criticality || "SIN_CLASIFICAR", source: relation?.source || "MANUAL", source_reference: relation?.source_reference || "", notes: relation?.notes || "", is_active: relation?.is_active !== false });
    setFormError("");
    setFormOpen(true);
  };
  const closeForm = () => { if (submitting) return; setFormOpen(false); setEditing(null); setForm(EMPTY_FORM); setSelectedMaterial(null); setMaterialQuery(""); setMaterialResults([]); setMaterialError(""); setFormError(""); };
  const chooseMaterial = (item) => { if (!materialRecordId(item) && !materialCode(item)) { setMaterialError("El resultado no contiene una identidad válida"); return; } setSelectedMaterial(item); setMaterialQuery([materialCode(item), materialDescription(item)].filter(Boolean).join(" · ")); setMaterialResults([]); setMaterialError(""); };

  const submitRelation = async (event) => {
    event.preventDefault();
    setFormError("");
    const quantity = Number(form.quantity_required);
    if (!Number.isFinite(quantity) || quantity <= 0) { setFormError("La cantidad requerida debe ser mayor que cero"); return; }
    const payload = { relationship_type: form.relationship_type, quantity_required: quantity, position_reference: optional(form.position_reference), usage_description: optional(form.usage_description), criticality: form.criticality, source: form.source, source_reference: optional(form.source_reference), notes: optional(form.notes), is_active: Boolean(form.is_active) };
    if (!editing) {
      if (!selectedMaterial) { setFormError("Selecciona un material del catálogo"); return; }
      const recordId = materialRecordId(selectedMaterial);
      const code = materialCode(selectedMaterial);
      if (recordId != null) payload.material_id = Number(recordId);
      else if (code) payload.material_code = code;
      else { setFormError("El material seleccionado no tiene identidad válida"); return; }
    }
    setSubmitting(true);
    const result = editing ? await actions.updateMachineSparePart(editing.id, payload) : await actions.createMachineSparePart(machineId, payload);
    if (!result?.success) { setFormError(errorOf(result, "No se pudo guardar la relación")); setSubmitting(false); return; }
    setSubmitting(false);
    closeForm();
    await loadRelations();
    await loadRelatedMaterials();
  };

  const toggleActive = async (relation) => {
    const nextActive = relation?.is_active === false;
    if (!window.confirm(nextActive ? "?Reactivar esta relación?" : "La relación se desactivar? sin perder trazabilidad. ?Continuar?")) return;
    const result = await actions.updateMachineSparePart(relation.id, { is_active: nextActive });
    if (!result?.success) { window.alert(errorOf(result, "No se pudo cambiar el estado")); return; }
    await loadRelations();
  };

  const relatedGroups = RELATED_GROUP_DEFINITIONS.map((definition) => ({
    ...definition,
    rows: array(relatedMaterials?.groups?.[definition.key]),
    total: Number(relatedMaterials?.summary?.[definition.key] || 0),
  })).filter((group) => group.rows.length > 0);

  const relatedTotal = relatedGroups.reduce(
    (totalCount, group) => totalCount + group.total,
    0,
  );

  if (machineLoading) return <main className="machine-detail-page"><div className="machine-state"><div className="spinner-border text-primary" role="status" /><p>Cargando ficha de máquina…</p></div></main>;
  if (machineError || !machine) return <main className="machine-detail-page"><div className="machine-state machine-state--error"><h1>No se pudo abrir la máquina</h1><p>{machineError || "La máquina no existe"}</p><button type="button" className="btn btn-primary" onClick={() => navigate("/machines")}>Volver al catálogo</button></div></main>;

  const unit = machine?.factory_unit || machine?.unit || null;
  const total = summary.total ?? summary.total_relations ?? pagination.total;
  const active = summary.active ?? summary.active_count ?? summary.active_relations ?? 0;
  const withStock = summary.with_stock ?? summary.with_stock_count ?? 0;
  const withDocuments = summary.with_documents ?? summary.with_documents_count ?? 0;

  return (
    <main className="machine-detail-page">
      <nav className="machine-breadcrumb" aria-label="Ruta de navegación"><Link to="/machines">Máquinas</Link><span>/</span><span>{machine.machine_code || machine.id}</span></nav>
      <header className="machine-detail-header">
        <div><div className="machine-detail-title-row"><span className="machine-code">{machine.machine_code || "Sin código"}</span><span className={`machine-badge machine-badge--status-${String(machine.status || "ACTIVA").toLowerCase()}`}>{machine.status || "Sin estado"}</span></div><h1>{machine.machine_name || "Máquina sin nombre"}</h1><p>{unit ? `${unit.unit_code || ""} ? ${unit.unit_name || ""}` : "Sin línea o sector"}</p></div>
        <Link to="/machines" className="btn btn-outline-secondary">Volver</Link>
      </header>

      <section className="machine-detail-grid">
        <article className="machine-detail-card"><h2>Datos técnicos</h2><dl className="machine-data-grid"><div><dt>Fabricante</dt><dd>{machine.manufacturer || "Sin dato"}</dd></div><div><dt>Modelo</dt><dd>{machine.model || "Sin dato"}</dd></div><div><dt>Serie</dt><dd>{machine.serial_number || "Sin dato"}</dd></div><div><dt>Ubicación</dt><dd>{machine.location_description || "Sin dato"}</dd></div><div><dt>Criticidad</dt><dd>{criticalityLabel(machine.criticality)}</dd></div><div><dt>Potencia</dt><dd>{machine.rated_power_kw != null ? `${number(machine.rated_power_kw)} kW` : "Sin dato"}</dd></div><div><dt>CE</dt><dd>{machine.ce_status || "Sin dato"}</dd></div><div><dt>Manual</dt><dd>{machine.manual_status || "Sin dato"}</dd></div></dl>{(machine.power_notes || machine.notes) && <div className="machine-detail-notes">{machine.power_notes && <p><strong>Potencia:</strong> {machine.power_notes}</p>}{machine.notes && <p><strong>Notas:</strong> {machine.notes}</p>}</div>}</article>
        <article className="machine-detail-card"><h2>Estado del despiece</h2><div className="machine-detail-summary"><div><span>Referencias</span><strong>{number(total)}</strong></div><div><span>Activas</span><strong>{number(active)}</strong></div><div><span>Con stock</span><strong>{number(withStock)}</strong></div><div><span>Con documentos</span><strong>{number(withDocuments)}</strong></div></div><p className="machine-detail-caption">El catálogo se completa progresivamente. No se calcula cobertura sin un BOM oficial.</p></article>
      </section>

      <section className="machine-list-card machine-related-section">
        <div className="machine-list-card__header">
          <div>
            <h2>Repuestos relacionados automáticamente</h2>
            <p>
              {relatedLoading
                ? "Analizando alias, nombres, modelos, marcas y línea…"
                : `${number(relatedTotal)} coincidencias explicadas`}
            </p>
          </div>
          <span className="machine-related-source">
            Fuente principal: alias del maestro ORMA
          </span>
        </div>

        {relatedError && (
          <div className="machine-state machine-state--error">
            <p>{relatedError}</p>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => loadRelatedMaterials()}
            >
              Reintentar
            </button>
          </div>
        )}

        {!relatedError && relatedLoading && (
          <div className="machine-state">
            <div className="spinner-border text-primary" role="status" />
            <p>Buscando repuestos relacionados con la máquina…</p>
          </div>
        )}

        {!relatedError && !relatedLoading && relatedGroups.length === 0 && (
          <div className="machine-state">
            <h3>No se encontraron coincidencias automáticas</h3>
            <p>
              La máquina puede seguir completándose mediante relaciones confirmadas.
            </p>
          </div>
        )}

        {!relatedError && !relatedLoading && relatedGroups.length > 0 && (
          <div className="machine-related-groups">
            {relatedGroups.map((group) => (
              <article className="machine-related-group" key={group.key}>
                <header className="machine-related-group__header">
                  <div>
                    <h3>{group.title}</h3>
                    <p>{group.description}</p>
                  </div>
                  <strong>{number(group.total)}</strong>
                </header>

                <div className="machine-related-list">
                  {group.rows.map((item) => {
                    const code = relatedCandidateCode(item);
                    const locations = relatedCandidateLocations(item);
                    const compatibleCount = Number(
                      item?.compatible_machine_count || 0,
                    );

                    return (
                      <div
                        className="machine-related-item"
                        key={`${group.key}-${item.material_record_id || code}`}
                      >
                        <div className="machine-related-item__identity">
                          <div className="machine-related-item__badges">
                            <span
                              className={`machine-related-badge machine-related-badge--${String(
                                item?.match_type || group.key,
                              ).toLowerCase()}`}
                            >
                              {group.badge}
                            </span>
                            {item?.is_confirmed && (
                              <span className="machine-related-badge machine-related-badge--confirmed">
                                Confirmado
                              </span>
                            )}
                          </div>
                          {code ? (
                            <Link
                              className="machine-material-link"
                              to={`/parts/${encodeURIComponent(code)}`}
                            >
                              {code}
                            </Link>
                          ) : (
                            <strong>Sin código</strong>
                          )}
                          <h4>{relatedCandidateDescription(item)}</h4>
                          {item?.alias && (
                            <p className="machine-related-alias">
                              <strong>Alias ORMA:</strong> {item.alias}
                            </p>
                          )}
                          <p className="machine-related-explanation">
                            {item?.explanation || "Coincidencia relacionada."}
                          </p>
                          {array(item?.matched_terms).length > 0 && (
                            <div className="machine-related-terms">
                              {array(item.matched_terms).map((term) => (
                                <span key={`${item.material_record_id}-${term}`}>
                                  {term}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="machine-related-item__operations">
                          <dl>
                            <div>
                              <dt>Stock ORMA</dt>
                              <dd>{number(item?.stock_total)}</dd>
                            </div>
                            <div>
                              <dt>Ubicaciones</dt>
                              <dd>{number(item?.location_count)}</dd>
                            </div>
                            <div>
                              <dt>Documentos</dt>
                              <dd>{number(item?.document_count)}</dd>
                            </div>
                            <div>
                              <dt>Compatibilidad</dt>
                              <dd>
                                {compatibleCount > 1
                                  ? `${number(compatibleCount)} máquinas`
                                  : "Esta máquina"}
                              </dd>
                            </div>
                          </dl>

                          {locations.length > 0 && (
                            <div className="machine-related-locations">
                              {locations.slice(0, 3).map((location, index) => (
                                <small
                                  key={`${item.material_record_id}-location-${index}`}
                                >
                                  {locationLabel(location)}
                                </small>
                              ))}
                              {locations.length > 3 && (
                                <small>
                                  +{locations.length - 3} ubicaciones
                                </small>
                              )}
                            </div>
                          )}

                          <div className="machine-row-actions">
                            {code && (
                              <Link
                                className="btn btn-sm btn-outline-primary"
                                to={`/parts/${encodeURIComponent(code)}`}
                              >
                                Ver material
                              </Link>
                            )}
                            {isAdmin && !item?.is_confirmed && (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => openSuggestedMaterial(item)}
                              >
                                Confirmar para esta máquina
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="machine-list-card machine-spare-section">
        <div className="machine-list-card__header"><div><h2>Repuestos confirmados</h2><p>{relationsLoading ? "Actualizando despiece?" : `${number(pagination.total)} relaciones`}</p></div>{isAdmin && <button type="button" className="btn btn-primary" onClick={openCreate}>Asociar repuesto</button>}</div>
        <form className="machine-spare-filters" onSubmit={(event) => { event.preventDefault(); setAppliedFilters({ ...draftFilters }); setPage(1); }}>
          <div className="machine-field machine-field--wide"><label htmlFor="spare-q">Buscar</label><input id="spare-q" className="form-control" value={draftFilters.q} onChange={(event) => setDraftFilters((current) => ({ ...current, q: event.target.value }))} placeholder="Código, descripción, posición o uso" /></div>
          <div className="machine-field"><label htmlFor="spare-type">Tipo</label><select id="spare-type" className="form-select" value={draftFilters.relationship_type} onChange={(event) => setDraftFilters((current) => ({ ...current, relationship_type: event.target.value }))}><option value="">Todos</option>{TYPES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select></div>
          <div className="machine-field"><label htmlFor="spare-criticality">Criticidad</label><select id="spare-criticality" className="form-select" value={draftFilters.criticality} onChange={(event) => setDraftFilters((current) => ({ ...current, criticality: event.target.value }))}><option value="">Todas</option>{CRITICALITIES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select></div>
          <div className="machine-field"><label htmlFor="spare-active">Relaciones</label><select id="spare-active" className="form-select" value={draftFilters.is_active} onChange={(event) => setDraftFilters((current) => ({ ...current, is_active: event.target.value }))}><option value="true">Activas</option><option value="false">Inactivas</option><option value="">Todas</option></select></div>
          <div className="machine-spare-filter-actions"><button type="submit" className="btn btn-primary">Aplicar</button><button type="button" className="btn btn-outline-secondary" onClick={() => { setDraftFilters(EMPTY_FILTERS); setAppliedFilters(EMPTY_FILTERS); setPage(1); }}>Limpiar</button></div>
        </form>

        {relationsError && <div className="machine-state machine-state--error"><p>{relationsError}</p><button type="button" className="btn btn-outline-primary" onClick={() => loadRelations()}>Reintentar</button></div>}
        {!relationsError && relationsLoading && <div className="machine-state"><div className="spinner-border text-primary" role="status" /><p>Cargando repuestos asociados…</p></div>}
        {!relationsError && !relationsLoading && relations.length === 0 && <div className="machine-state"><h3>No hay repuestos confirmados</h3><p>Las sugerencias automáticas aparecen en la sección anterior. Confirma únicamente las relaciones verificadas.</p>{isAdmin && <button type="button" className="btn btn-primary" onClick={openCreate}>Confirmar repuesto manualmente</button>}</div>}
        {!relationsError && !relationsLoading && relations.length > 0 && <><div className="machine-table-wrap"><table className="table machine-table machine-spare-table"><thead><tr><th>Material</th><th>Tipo</th><th>Cantidad</th><th>Posición / uso</th><th>Criticidad</th><th>Stock</th><th>Ubicaciones</th><th>Documentos</th><th>Estado</th><th>Acciones</th></tr></thead><tbody>{relations.map((relation) => { const material = materialOf(relation); const code = materialCode(material); const locations = locationsOf(relation); return <tr key={relation.id}><td data-label="Material">{code ? <Link className="machine-material-link" to={`/parts/${encodeURIComponent(code)}`}>{code}</Link> : "Sin código"}<small>{materialDescription(material)}</small></td><td data-label="Tipo">{typeLabel(relation.relationship_type)}</td><td data-label="Cantidad">{number(relation.quantity_required)}</td><td data-label="Posición / uso"><span>{relation.position_reference || "Sin posición"}</span><small>{relation.usage_description || "Sin descripción de uso"}</small></td><td data-label="Criticidad">{criticalityLabel(relation.criticality)}</td><td data-label="Stock"><strong>{number(stockOf(relation))}</strong></td><td data-label="Ubicaciones">{locations.length ? <>{locations.slice(0, 2).map((location, index) => <small key={`${relation.id}-${index}`}>{locationLabel(location)}</small>)}{locations.length > 2 && <small>+{locations.length - 2} ubicaciones</small>}</> : "Sin ubicación"}</td><td data-label="Documentos">{documentsOf(relation)}</td><td data-label="Estado">{relation.is_active === false ? "Inactiva" : "Activa"}</td><td data-label="Acciones"><div className="machine-row-actions">{code && <Link className="btn btn-sm btn-outline-primary" to={`/parts/${encodeURIComponent(code)}`}>Ver repuesto</Link>}{isAdmin && <><button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openEdit(relation)}>Editar</button><button type="button" className="btn btn-sm btn-outline-danger" onClick={() => toggleActive(relation)}>{relation.is_active === false ? "Reactivar" : "Desactivar"}</button></>}</div></td></tr>; })}</tbody></table></div><nav className="machine-pagination" aria-label="Paginación de repuestos"><button type="button" className="btn btn-outline-secondary" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page <= 1}>Anterior</button><span>Página {pagination.page} de {pagination.totalPages}</span><button type="button" className="btn btn-outline-secondary" onClick={() => setPage((current) => Math.min(pagination.totalPages, current + 1))} disabled={page >= pagination.totalPages}>Siguiente</button></nav></>}
      </section>

      {formOpen && <div className="machine-modal-backdrop"><section className="machine-modal machine-modal--wide" role="dialog" aria-modal="true" aria-labelledby="relation-form-title"><header className="machine-modal__header"><div><h2 id="relation-form-title">{editing ? "Editar relación" : "Asociar repuesto"}</h2><p>Máquina y material quedan inmutables tras el alta.</p></div><button type="button" className="btn-close" aria-label="Cerrar" onClick={closeForm} disabled={submitting} /></header><form onSubmit={submitRelation}><div className="machine-modal__body machine-form-grid">
        <div className="machine-field machine-field--full"><label htmlFor="relation-material">Material</label>{editing ? <div className="machine-selected-material"><strong>{materialCode(selectedMaterial) || "Sin código"}</strong><span>{materialDescription(selectedMaterial)}</span></div> : <div className="machine-material-search"><input id="relation-material" className="form-control" value={materialQuery} onChange={(event) => { setMaterialQuery(event.target.value); setSelectedMaterial(null); }} placeholder="Código, descripción, referencia, marca o modelo" autoComplete="off" />{materialSearching && <small>Buscando?</small>}{materialError && <span className="machine-field-error">{materialError}</span>}{materialResults.length > 0 && <div className="machine-material-results">{materialResults.map((item, index) => <button type="button" key={materialRecordId(item) ?? materialCode(item) ?? index} onClick={() => chooseMaterial(item)}><strong>{materialCode(item) || "Sin código"}</strong><span>{materialDescription(item)}</span></button>)}</div>}{selectedMaterial && <div className="machine-selected-material"><strong>{materialCode(selectedMaterial) || "Sin código"}</strong><span>{materialDescription(selectedMaterial)}</span></div>}</div>}</div>
        <div className="machine-field"><label htmlFor="relation-type">Tipo</label><select id="relation-type" className="form-select" value={form.relationship_type} onChange={(event) => setForm((current) => ({ ...current, relationship_type: event.target.value }))}>{TYPES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select></div>
        <div className="machine-field"><label htmlFor="relation-quantity">Cantidad requerida</label><input id="relation-quantity" type="number" min="0.001" step="0.001" className="form-control" value={form.quantity_required} onChange={(event) => setForm((current) => ({ ...current, quantity_required: event.target.value }))} /></div>
        <div className="machine-field"><label htmlFor="relation-position">Posición / referencia</label><input id="relation-position" className="form-control" value={form.position_reference} onChange={(event) => setForm((current) => ({ ...current, position_reference: event.target.value }))} /></div>
        <div className="machine-field"><label htmlFor="relation-criticality">Criticidad</label><select id="relation-criticality" className="form-select" value={form.criticality} onChange={(event) => setForm((current) => ({ ...current, criticality: event.target.value }))}>{CRITICALITIES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select></div>
        <div className="machine-field machine-field--full"><label htmlFor="relation-use">Descripción de uso</label><textarea id="relation-use" className="form-control" rows="2" value={form.usage_description} onChange={(event) => setForm((current) => ({ ...current, usage_description: event.target.value }))} /></div>
        <div className="machine-field"><label htmlFor="relation-source">Origen</label><select id="relation-source" className="form-select" value={form.source} onChange={(event) => setForm((current) => ({ ...current, source: event.target.value }))} disabled={!editing}><option value="MANUAL">Manual</option><option value="IMPORTACION">Importación</option><option value="HISTORICO">Histórico</option></select></div>
        <div className="machine-field"><label htmlFor="relation-source-reference">Referencia de origen</label><input id="relation-source-reference" className="form-control" value={form.source_reference} onChange={(event) => setForm((current) => ({ ...current, source_reference: event.target.value }))} /></div>
        <div className="machine-field machine-field--full"><label htmlFor="relation-notes">Notas</label><textarea id="relation-notes" className="form-control" rows="3" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} /></div>
        {editing && <label className="machine-check machine-field--full"><input type="checkbox" checked={form.is_active} onChange={(event) => setForm((current) => ({ ...current, is_active: event.target.checked }))} />Relación activa</label>}
        {formError && <div className="alert alert-danger machine-field--full" role="alert">{formError}</div>}
      </div><footer className="machine-modal__footer"><button type="button" className="btn btn-outline-secondary" onClick={closeForm} disabled={submitting}>Cancelar</button><button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? "Guardando…" : "Guardar relación"}</button></footer></form></section></div>}
    </main>
  );
};

export default MachineDetail;
